import React from "react";
import { MapPin } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Reveal, Kicker } from "../motion";
import regionMapTablet from "../../assets/sections/region-map-tablet.webp";

export function Geography() {
  const { c } = useApp();
  const g = c.geo;

  return (
    <section data-testid="geo-section" className="relative bg-[#f5f7fa] py-24 md:py-32">
      <div className="mx-auto grid max-w-[1240px] items-center gap-14 px-5 md:px-8 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <Kicker>{g.kicker}</Kicker>
          <h2 className="mt-5 font-display text-4xl font-bold tracking-tight text-[#0a1a2e] sm:text-5xl">
            {g.title}
          </h2>
          <p className="mt-4 max-w-lg text-base text-[#3e516a] md:text-lg">{g.subtitle}</p>

          <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#002355] px-4 py-2 text-sm font-semibold text-[#f5f7fa]">
            <MapPin size={16} className="text-[#e8a93d]" />
            {g.badge}
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {g.districts.map((d) => (
              <span
                key={d}
                className="rounded-full border border-[#002355]/15 bg-white px-3 py-1.5 text-sm text-[#24313f] transition-colors duration-300 hover:border-[#002355]/40"
              >
                {d}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="relative">
          <div className="grain relative aspect-[16/10] overflow-hidden rounded-[2rem] bg-[#002355]">
            <img
              src={regionMapTablet}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="h-full w-full origin-right scale-[1.35] object-cover object-right"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function RegionMap() {
  return (
    <svg viewBox="0 0 400 400" className="h-full w-full" fill="none">
      {/* subtle grid */}
      {Array.from({ length: 8 }).map((_, i) => (
        <line key={`h${i}`} x1="0" y1={i * 55} x2="400" y2={i * 55} stroke="#f5f7fa" strokeOpacity="0.06" />
      ))}
      {Array.from({ length: 8 }).map((_, i) => (
        <line key={`v${i}`} x1={i * 55} y1="0" x2={i * 55} y2="400" stroke="#f5f7fa" strokeOpacity="0.06" />
      ))}

      {/* West Kazakhstan region silhouette, traced from the real administrative
          boundary (OpenStreetMap relation 215441), simplified for display */}
      <path
        d="M30.0 293.2 L59.8 305.0 L129.8 315.6 L154.9 324.8 L152.8 318.6 L176.1 316.0 L186.9 303.1 L215.9 286.0 L221.0 276.5 L244.4 274.4 L253.3 276.9 L251.9 283.9 L284.9 287.9 L287.1 271.8 L291.5 271.9 L291.2 256.5 L294.4 257.6 L296.2 253.1 L320.8 252.8 L319.7 249.8 L329.8 241.0 L332.4 225.7 L329.2 221.7 L333.6 223.0 L338.2 217.0 L356.5 218.5 L360.7 215.5 L358.1 212.5 L367.1 208.0 L365.6 202.7 L370.0 190.5 L363.8 184.4 L360.9 170.2 L369.3 157.3 L362.9 150.5 L367.3 135.3 L357.7 132.3 L347.9 115.8 L331.6 111.1 L329.9 100.2 L320.1 93.7 L311.3 90.9 L301.4 96.1 L293.8 91.7 L285.1 96.3 L276.6 75.2 L265.3 82.9 L255.5 81.9 L251.8 87.8 L253.6 93.4 L247.0 96.5 L244.8 89.9 L240.2 94.2 L231.5 94.0 L229.6 89.1 L235.4 89.1 L235.4 84.0 L230.9 81.1 L215.0 81.4 L214.0 76.7 L209.7 75.4 L211.5 87.6 L206.0 88.3 L208.2 84.8 L202.0 83.7 L200.8 95.0 L193.0 97.9 L193.1 104.1 L177.7 110.0 L168.0 118.8 L154.5 117.6 L149.6 126.6 L153.2 135.7 L140.7 139.8 L127.6 152.2 L117.3 149.8 L121.3 153.3 L127.3 179.9 L125.4 184.6 L131.3 189.8 L124.7 196.1 L111.9 204.0 L102.9 199.6 L97.4 184.5 L77.0 160.7 L64.2 170.9 L65.4 175.5 L62.1 178.5 L66.2 184.9 L59.0 195.2 L47.2 199.9 L44.5 216.7 L42.1 234.1 L51.1 241.1 L52.5 250.4 L42.0 259.4 L30.0 293.2 Z"
        fill="#e8a93d"
        fillOpacity="0.08"
        stroke="#e8a93d"
        strokeOpacity="0.45"
        strokeWidth="1.5"
      />

      {/* Uralsk pulse point */}
      <g transform="translate(235,111)">
        <circle r="26" fill="#e8a93d" fillOpacity="0.14">
          <animate attributeName="r" values="16;30;16" dur="2.6s" repeatCount="indefinite" />
          <animate attributeName="fill-opacity" values="0.22;0;0.22" dur="2.6s" repeatCount="indefinite" />
        </circle>
        <circle r="7" fill="#e8a93d" />
        <text x="16" y="5" fill="#f5f7fa" fontSize="14" fontWeight="700" fontFamily="Manrope">
          Орал
        </text>
      </g>
    </svg>
  );
}
